import { useState, useEffect } from 'react'
import { createClient } from '@supabase/supabase-js'
import axios from 'axios'
import { Capacitor } from '@capacitor/core'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

const API = process.env.REACT_APP_API_URL

const BANDERAS = [
  { codigo:'co', pais:'Colombia' },
  { codigo:'ca', pais:'Canada' },
  { codigo:'us', pais:'United States' },
  { codigo:'br', pais:'Brazil' },
  { codigo:'jp', pais:'Japan' },
  { codigo:'cn', pais:'China' },
  { codigo:'gb', pais:'United Kingdom' },
  { codigo:'fr', pais:'France' },
  { codigo:'pt', pais:'Portugal' },
]

function Bandera({ codigo, pais, size = 22 }) {
  return (
    <img
      src={'https://flagcdn.com/24x18/' + codigo + '.png'}
      alt={pais}
      title={pais}
      style={{ width: size, height: Math.round(size * 0.75), borderRadius: 3, objectFit:'cover', boxShadow:'0 1px 3px rgba(0,0,0,0.4)', flexShrink:0 }}
    />
  )
}

/* Icono de ojo (mostrar/ocultar contrasena) -- SVG simple, sin dependencias */
function IconoOjo({ abierto, size = 18 }) {
  return abierto ? (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a20.3 20.3 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a20.3 20.3 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
}

const EMOJIS_AVATAR = [
  '😀','🧑‍💻','🧑‍🎓','🦸','🧑‍🎨','👩‍🚀','🧑‍🍳','🧑‍🏫',
  '🐱','🐶','🦊','🐼','🦁','🐨','🐸','🦉',
  '🐧','🦄','🌟','🔥','🎯','📚','🎸','⚽'
]

/* Hook simple para detectar si estamos en tamaño movil */
function useEsMovil() {
  const [esMovil, setEsMovil] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 820 : false
  )
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)')
    const handler = e => setEsMovil(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return esMovil
}

/* EspañolGo es para personas que todavia NO hablan nada de español --
   por eso esta pantalla (a diferencia del resto de la plataforma, que
   arranca en español) detecta el idioma del navegador y arranca en
   ingles salvo que el navegador este configurado en español. Siempre
   se puede cambiar a mano con el selector ES/EN de la esquina. */
function detectarIdiomaInicial() {
  try {
    const lang = (navigator.language || navigator.languages?.[0] || 'en').toLowerCase()
    return lang.startsWith('es') ? 'es' : 'en'
  } catch (err) {
    return 'en'
  }
}

const TEXTOS = {
  es: {
    volver: '← Volver',
    planSeleccionado: 'Plan seleccionado',
    bienvenido: 'Bienvenido',
    seleccionaAcceso: 'Selecciona como quieres acceder',
    rolEstudianteLabel: 'Estudiante',
    rolEstudianteDesc: 'Accede a tu curriculo, clases y retos',
    rolProfesorLabel: 'Profesor',
    rolProfesorDesc: 'Gestiona tus clases y estudiantes',
    rolAdminLabel: 'Admin',
    rolAdminDesc: 'Panel de administracion completo',
    accesoTitulo: label => 'Acceso ' + label,
    ingresaCredenciales: 'Ingresa tus credenciales',
    email: 'Email',
    password: 'Password',
    placeholderEmail: 'tu@email.com',
    placeholderPassword: 'Minimo 6 caracteres',
    ocultarPassword: 'Ocultar contraseña',
    mostrarPassword: 'Mostrar contraseña',
    eligeAvatar: 'Elige tu avatar',
    soloAlCrear: 'Solo aplica si vas a crear una cuenta nueva',
    elegirEmoji: '😀 Elegir Emoji',
    subirFoto: '📷 Subir Foto',
    ocultar: 'Ocultar',
    seleccionarEmoji: 'Seleccionar emoji',
    cambiarFoto: 'Cambiar foto',
    seleccionarFoto: 'Seleccionar foto',
    formatoFoto: 'JPG o PNG, maximo 3MB',
    cargando: 'Cargando...',
    ingresarComoTitulo: label => 'Ingresar como ' + label,
    subiendoFoto: 'Subiendo foto...',
    crearCuentaGratis: 'Create free account',
    yaTienesCuenta: '¿Ya tienes cuenta? Inicia sesion',
    eresNuevo: '¿Eres nuevo? Crea tu cuenta gratis',
    errorAdmin: 'No tienes permisos de administrador.',
    errorProfesor: 'No eres un profesor registrado en EspañolGo.',
    errorConexion: 'Error de conexion. Intenta de nuevo.',
    errorCampos: 'Ingresa email y contrasena',
    errorImagen: 'La imagen no puede pesar mas de 3MB',
    revisaEmailPago: nombre => 'Revisa tu email para confirmar tu cuenta. Cuando la confirmes, vuelve a entrar con el mismo correo para completar el pago del plan ' + nombre + '.',
    revisaEmailExamen: 'Revisa tu email para confirmar tu cuenta. Un profesor revisara tu examen pronto.',
    revisaEmail: 'Revisa tu email para confirmar tu cuenta.'
  },
  en: {
    volver: '← Back',
    planSeleccionado: 'Selected plan',
    bienvenido: 'Welcome',
    seleccionaAcceso: 'Choose how you want to sign in',
    rolEstudianteLabel: 'Student',
    rolEstudianteDesc: 'Access your curriculum, classes and challenges',
    rolProfesorLabel: 'Teacher',
    rolProfesorDesc: 'Manage your classes and students',
    rolAdminLabel: 'Admin',
    rolAdminDesc: 'Full admin panel',
    accesoTitulo: label => 'Sign in as ' + label,
    ingresaCredenciales: 'Enter your credentials',
    email: 'Email',
    password: 'Password',
    placeholderEmail: 'you@email.com',
    placeholderPassword: 'Minimum 6 characters',
    ocultarPassword: 'Hide password',
    mostrarPassword: 'Show password',
    eligeAvatar: 'Choose your avatar',
    soloAlCrear: 'Only applies if you are creating a new account',
    elegirEmoji: '😀 Choose Emoji',
    subirFoto: '📷 Upload Photo',
    ocultar: 'Hide',
    seleccionarEmoji: 'Choose emoji',
    cambiarFoto: 'Change photo',
    seleccionarFoto: 'Choose photo',
    formatoFoto: 'JPG or PNG, max 3MB',
    cargando: 'Loading...',
    ingresarComoTitulo: label => 'Sign in as ' + label,
    subiendoFoto: 'Uploading photo...',
    crearCuentaGratis: 'Create free account',
    yaTienesCuenta: 'Already have an account? Sign in',
    eresNuevo: 'New here? Create your free account',
    errorAdmin: 'You do not have administrator permissions.',
    errorProfesor: 'You are not a registered teacher in EspañolGo.',
    errorConexion: 'Connection error. Please try again.',
    errorCampos: 'Enter your email and password',
    errorImagen: 'The image cannot be larger than 3MB',
    revisaEmailPago: nombre => 'Check your email to confirm your account. Once confirmed, log back in with the same email to complete payment for the ' + nombre + ' plan.',
    revisaEmailExamen: 'Check your email to confirm your account. A teacher will review your test soon.',
    revisaEmail: 'Check your email to confirm your account.'
  }
}

export default function Login({ onLogin, planElegido, datosRevision, onVolver, soloEstudiante }) {
  // Dentro de la app instalada (Android/iOS) solo el estudiante inicia
  // sesion aqui: el profesor siempre da clase desde laptop/PC, por eso ni
  // siquiera se le muestra la opcion de rol dentro de la app — se entra
  // directo al formulario de estudiante. Lo mismo pasa en el sitio web
  // cuando se llega aqui por el boton "Get Started" del landing (prop
  // soloEstudiante): ese boton es solo para estudiantes nuevos, asi que no
  // tiene sentido mostrarle la lista de roles primero.
  const esApp = Capacitor.isNativePlatform()
  const soloRolEstudiante = esApp || soloEstudiante
  const [rol,      setRol]      = useState(() => soloRolEstudiante ? 'estudiante' : null)
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [mostrarPassword, setMostrarPassword] = useState(false)
  const [error,    setError]    = useState('')
  const [carga,    setCarga]    = useState(false)
  const esMovil = useEsMovil()
  const [idioma, setIdioma] = useState(detectarIdiomaInicial)
  const t = TEXTOS[idioma]

  // Avatar
  const [tipoAvatar,     setTipoAvatar]     = useState('emoji') // 'emoji' | 'foto'
  const [emojiElegido,   setEmojiElegido]   = useState(EMOJIS_AVATAR[0])
  const [archivoFoto,    setArchivoFoto]    = useState(null)
  const [previewFoto,    setPreviewFoto]    = useState(null)
  const [subiendoFoto,   setSubiendoFoto]   = useState(false)
  // La grilla de emojis empieza cerrada: solo se despliega cuando el
  // usuario le da clic a "Seleccionar emoji", en vez de mostrar los 24 de
  // una vez.
  const [emojiGridAbierto, setEmojiGridAbierto] = useState(false)

  // Flujo de estudiante nuevo vs. estudiante que ya tiene cuenta.
  // Por defecto un visitante nuevo solo ve "Create free account" (no tiene
  // sentido mostrarle "Ingresar como Estudiante" si todavia no existe su
  // cuenta). Una vez que confirma su correo (ver EmailConfirmado.jsx, que
  // guarda esta marca en localStorage) este mismo navegador recuerda que ya
  // tiene cuenta y muestra "Ingresar" de una vez. Tambien dejamos un enlace
  // para cambiar de modo a mano, por si alguien entra desde otro
  // dispositivo o borro los datos del navegador.
  const [modoEstudiante, setModoEstudiante] = useState(() => {
    try {
      return window.localStorage.getItem('espanolgo_email_confirmado') === '1' ? 'ingresar' : 'crear'
    } catch (err) {
      return 'crear'
    }
  })

  // Acceso admin oculto: solo aparece si la URL trae ?admin=1
  const [adminOculto] = useState(() => {
    if (typeof window === 'undefined') return false
    const params = new URLSearchParams(window.location.search)
    return params.get('admin') === '1'
  })

  function handleSeleccionarFoto(e) {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 3 * 1024 * 1024) {
      setError(t.errorImagen)
      return
    }
    setArchivoFoto(file)
    setPreviewFoto(URL.createObjectURL(file))
    setError('')
  }

  // El <form> siempre dispara este submit (por ejemplo al dar Enter en el
  // password). Si el estudiante esta en modo "crear cuenta" (el boton de
  // Ingresar esta oculto) lo mandamos a crear la cuenta en vez de intentar
  // iniciar sesion con una cuenta que todavia no existe.
  function handleSubmitForm(e) {
    e.preventDefault()
    if (rol === 'estudiante' && modoEstudiante === 'crear') {
      handleRegistro()
    } else {
      handleSubmit(e)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setCarga(true); setError('')
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) { setError(error.message); setCarga(false); return }

      if (rol === 'admin') {
        const res = await axios.get(API + '/curriculo/admin/verificar/' + email)
        if (!res.data.esAdmin) { setError(t.errorAdmin); setCarga(false); return }
        onLogin(data.user, 'admin')
      } else if (rol === 'profesor') {
        const res = await axios.get(API + '/curriculo/profesor/verificar/' + email)
        if (!res.data.esProfesor) { setError(t.errorProfesor); setCarga(false); return }
        onLogin(data.user, 'profesor', res.data.profesor)
      } else {
        onLogin(data.user, 'estudiante')
      }
    } catch (err) {
      setError(t.errorConexion)
      setCarga(false)
    }
  }

  async function handleRegistro() {
    if (!email || !password) { setError(t.errorCampos); return }
    setCarga(true); setError('')

    const redirectUrl = window.location.origin + '/email-confirmado'

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl }
    })

    if (error) { setError(error.message); setCarga(false); return }

    if (data?.user?.id) {
      let avatarUrl = null

      if (tipoAvatar === 'foto' && archivoFoto) {
        setSubiendoFoto(true)
        try {
          const extension = archivoFoto.name.split('.').pop()
          const rutaArchivo = data.user.id + '/avatar.' + extension

          const { error: errorSubida } = await supabase.storage
            .from('avatars')
            .upload(rutaArchivo, archivoFoto, { upsert: true })

          if (!errorSubida) {
            const { data: urlData } = supabase.storage
              .from('avatars')
              .getPublicUrl(rutaArchivo)
            avatarUrl = urlData.publicUrl
          }
        } catch (err) {
          console.error('Error subiendo avatar:', err)
        }
        setSubiendoFoto(false)
      }

      try {
        await axios.post(API + '/progreso/inicializar-usuario', {
          usuario_id: data.user.id,
          email: email,
          avatar_emoji: tipoAvatar === 'emoji' ? emojiElegido : null,
          avatar_url: tipoAvatar === 'foto' ? avatarUrl : null
        })
      } catch (err) {
        console.error('Error inicializando usuario:', err)
      }

      // Guarda el plan que eligio (si vino de la pantalla de precios)
      if (planElegido) {
        try {
          await supabase.from('registros_plan').insert({
            usuario_id: data.user.id,
            email: email,
            plan_id: planElegido.id,
            plan_nombre: planElegido.nombre,
            plan_precio: planElegido.precio
          })
        } catch (err) {
          console.error('Error guardando plan elegido:', err)
        }
      }

      // Notifica a Ivan por correo del nuevo registro
      try {
        await axios.post(API + '/notificaciones/nuevo-registro', {
          email: email,
          plan_nombre: planElegido?.nombre,
          plan_precio: planElegido?.precio
        })
      } catch (err) {
        console.error('Error enviando notificacion:', err)
      }

      // Si eligio el plan trial, le arrancamos ya la prueba gratis de 7
      // dias. Si eligio un plan pago, esto no aplica — pagara con PayPal
      // en la pantalla de Pago justo despues de este registro (o despues
      // de confirmar su correo, si el proyecto de Supabase lo exige).
      if (planElegido?.id === 'trial') {
        try {
          await axios.post(API + '/pagos/iniciar-prueba', { usuario_id: data.user.id })
        } catch (err) {
          console.error('Error iniciando prueba gratis:', err)
        }
      }

      // Si venia de un examen de nivel con escritura o grabaciones de voz
      // pendientes, las enviamos ahora a la cola de revision docente
      // (correo + panel de admin) — igual que en EnglishBridge. Antes
      // EspañolGo no capturaba nada de esto: el examen se autocalificaba
      // solo y no quedaba ningun registro para que un profesor confirmara
      // el nivel real de Writing/Speaking.
      if (datosRevision && (datosRevision.escritura?.length || datosRevision.grabaciones?.length)) {
        try {
          const form = new FormData()
          form.append('email', email)
          form.append('usuario_id', data.user.id)
          form.append('escritura', JSON.stringify(
            (datosRevision.escritura || []).map(e => ({ pregunta: e.pregunta, nivel: e.nivel, texto: e.texto }))
          ))
          form.append('temas_speaking', JSON.stringify(
            (datosRevision.grabaciones || []).map(g => ({ pregunta_id: g.pregunta_id, nivel: g.nivel, tema: g.tema, duracion: g.duracion }))
          ))
          ;(datosRevision.grabaciones || []).forEach(g => {
            if (g.blob) form.append('audios', g.blob, g.pregunta_id + '_' + g.nivel + '.webm')
          })

          await axios.post(API + '/examen-revision/enviar', form, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        } catch (err) {
          console.error('Error enviando examen a revision:', err)
        }
      }
    }

    // Si el proyecto de Supabase tiene desactivada la confirmacion por
    // correo, signUp ya devuelve una sesion activa: seguimos de una vez
    // (a Pago si eligio un plan pago, o directo a la plataforma si era
    // trial) — igual que en EnglishBridge. Antes esto NUNCA pasaba aqui:
    // sin importar si ya habia sesion activa, siempre se mostraba "revisa
    // tu email" y el estudiante quedaba obligado a un paso extra (revisar
    // correo y volver a iniciar sesion) para llegar al pago, aunque no
    // hiciera falta. Si Supabase SI exige confirmar el correo primero, no
    // hay sesion todavia y mostramos el mensaje de siempre.
    if (data.session) {
      onLogin(data.user, 'estudiante')
      return
    }

    setError(
      planElegido && planElegido.id !== 'trial'
        ? t.revisaEmailPago(planElegido.nombre)
        : datosRevision && (datosRevision.escritura?.length || datosRevision.grabaciones?.length)
          ? t.revisaEmailExamen
          : t.revisaEmail
    )
    setCarga(false)
  }

  const letras = [
    { l:'E', c1:'#FFE566', c2:'#FFD700' },
    { l:'s', c1:'#FFE566', c2:'#FFD700' },
    { l:'p', c1:'#FFE566', c2:'#FFD700' },
    { l:'a', c1:'#FFE566', c2:'#FFD700' },
    { l:'ñ', c1:'#7BA7E8', c2:'#4A7FD4' },
    { l:'o', c1:'#7BA7E8', c2:'#4A7FD4' },
    { l:'l', c1:'#7BA7E8', c2:'#4A7FD4' },
    { l:'G', c1:'#FF8A8A', c2:'#E84545' },
    { l:'o', c1:'#FF8A8A', c2:'#E84545' },
  ]

  // ROLES publicos: SOLO Estudiante y Profesor (Admin quitado del reporte de UX, item urgente #12).
  // Dentro de la app instalada, o al entrar por "Get Started" (solo
  // estudiantes nuevos), solo Estudiante — el profesor da clase desde
  // laptop/PC (navegador), nunca desde la app movil ni desde ese boton.
  const ROLES = soloRolEstudiante
    ? [
        { id:'estudiante', ic:'🎓', label:t.rolEstudianteLabel, desc:t.rolEstudianteDesc, color:'#1B3A8C' },
      ]
    : [
        { id:'estudiante', ic:'🎓', label:t.rolEstudianteLabel, desc:t.rolEstudianteDesc, color:'#1B3A8C' },
        { id:'profesor',   ic:'👨‍🏫', label:t.rolProfesorLabel,   desc:t.rolProfesorDesc,   color:'#059669' },
      ]
  if (!soloRolEstudiante && adminOculto) {
    ROLES.push({ id:'admin', ic:'⚙️', label:t.rolAdminLabel, desc:t.rolAdminDesc, color:'#EF4444' })
  }

  return (
    <div style={{ minHeight:'100vh', display: esMovil ? 'block' : 'flex', position:'relative', overflow:'hidden', fontFamily:'system-ui, sans-serif' }}>

      {/* Fondo */}
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(145deg, #0a1628 0%, #0f2347 40%, #1a1248 70%, #0d1f3c 100%)', zIndex:0 }} />

      {/* Patron geometrico */}
      <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', zIndex:1, opacity:0.12 }} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="geo" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <polygon points="30,2 58,16 58,44 30,58 2,44 2,16" fill="none" stroke="#FFD700" strokeWidth="0.8"/>
            <polygon points="30,12 48,21 48,39 30,48 12,39 12,21" fill="none" stroke="#4A90D9" strokeWidth="0.5"/>
            <circle cx="30" cy="30" r="2" fill="#FFD700" opacity="0.6"/>
          </pattern>
          <pattern id="dots" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
            <circle cx="15" cy="15" r="1" fill="#ffffff" opacity="0.4"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#dots)"/>
        <rect width="100%" height="100%" fill="url(#geo)"/>
      </svg>

      {/* Esferas */}
      <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', zIndex:2, opacity:0.18 }} xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="12%" cy="20%" rx="180" ry="180" fill="#FFD700" opacity="0.15"/>
        <ellipse cx="12%" cy="20%" rx="90"  ry="90"  fill="#003893" opacity="0.2"/>
        <ellipse cx="75%" cy="75%" rx="220" ry="220" fill="#009C3B" opacity="0.12"/>
        <ellipse cx="75%" cy="75%" rx="110" ry="110" fill="#FEDD00" opacity="0.15"/>
        <ellipse cx="55%" cy="10%" rx="140" ry="140" fill="#CE1126" opacity="0.1"/>
        <line x1="0" y1="100%" x2="40%" y2="0" stroke="#FFD700" strokeWidth="1" opacity="0.2"/>
        <line x1="20%" y1="100%" x2="60%" y2="0" stroke="#4A90D9" strokeWidth="1" opacity="0.15"/>
        <line x1="60%" y1="100%" x2="100%" y2="20%" stroke="#FFD700" strokeWidth="1" opacity="0.12"/>
      </svg>

      <div style={{ position:'absolute', inset:0, background:'linear-gradient(90deg, rgba(10,22,40,0.55) 0%, rgba(10,22,40,0.3) 60%, rgba(10,22,40,0.85) 100%)', zIndex:3 }} />

      {/* Panel izquierdo (branding) */}
      <div style={{ flex: esMovil ? 'none' : 1, display:'flex', alignItems:'center', justifyContent:'center', padding: esMovil ? '40px 24px 24px' : '48px', position:'relative', zIndex:4 }}>
        <div style={{ color:'#fff', maxWidth:'440px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom: esMovil ? 16 : 24, flexWrap:'wrap' }}>
            <span style={{ fontSize: esMovil ? 24 : 30, marginRight:4 }}>🌎</span>
            {BANDERAS.map((b,i) => <Bandera key={i} codigo={b.codigo} pais={b.pais} size={esMovil ? 18 : 22} />)}
          </div>

          {/* Logo */}
          <div style={{ display:'flex', alignItems:'baseline', marginBottom: esMovil ? 14 : 22, gap:'0px', marginLeft:'-2px' }}>
            {letras.map((lt, i) => (
              <svg key={i} width={esMovil ? 26 : 36} height={esMovil ? 45 : 62} viewBox="0 0 36 62" style={{ overflow:'visible', flexShrink:0, marginRight: i === 6 ? '4px' : '-1px' }}>
                <defs>
                  <linearGradient id={'wv' + i} x1="0%" y1="110%" x2="0%" y2="-10%">
                    <stop offset="0%"   stopColor={lt.c2} />
                    <stop offset="45%"  stopColor={lt.c1} />
                    <stop offset="70%"  stopColor={lt.c2} stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.2" />
                  </linearGradient>
                  <clipPath id={'cp' + i}>
                    <text x="1" y="52" fontSize="52" fontWeight="900" fontFamily="system-ui, sans-serif">{lt.l}</text>
                  </clipPath>
                </defs>
                <rect x="-4" y="-4" width="44" height="70" fill={'url(#wv' + i + ')'} clipPath={'url(#cp' + i + ')'} />
                <path
                  d={'M-4,' + (26 + Math.sin(i * 1.1) * 7) + ' Q9,' + (19 + Math.sin(i*1.1)*5) + ' 18,' + (26 + Math.sin(i*1.1)*7) + ' Q27,' + (33 + Math.sin(i*1.1)*5) + ' 40,' + (26 + Math.sin(i*1.1)*7)}
                  fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.2"
                  clipPath={'url(#cp' + i + ')'}
                />
              </svg>
            ))}
          </div>

          {!esMovil && (
            <div style={{ display:'flex', flexDirection:'column', gap:'11px', marginBottom:'32px' }}>
              {[
                { ic:'🎥', text:'Live classes with native Spanish teachers'     },
                { ic:'💬', text:'VideoForums: real conversation practice'        },
                { ic:'👤', text:'Personalized 1-on-1 private lessons'            },
                { ic:'🏆', text:'A1 to C2 - full European Framework curriculum' },
              ].map((item, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <span style={{ fontSize:'17px' }}>{item.ic}</span>
                  <span style={{ fontSize:'14px', opacity:.88 }}>{item.text}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display:'flex', alignItems:'center', gap:'10px', opacity:.7 }}>
            <div style={{ width:'38px', height:'38px', borderRadius:'8px', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <img src="/edunova-logo.png" alt="EduNova" style={{ width:'34px', height:'34px', objectFit:'contain' }} />
            </div>
            <span style={{ fontSize:'11px', letterSpacing:'1.2px', textTransform:'uppercase' }}>by EduNova Corporation</span>
          </div>
        </div>
      </div>

      {/* Panel derecho (formulario) */}
      <div style={{ width: esMovil ? '100%' : '460px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', padding: esMovil ? '32px 20px 48px' : '40px 48px', position:'relative', zIndex:4, overflowY:'auto', boxSizing:'border-box' }}>
        <div style={{ width:'100%' }}>

          {/* Selector de idioma ES/EN -- EspañolGo es para gente que
              todavia no habla nada de español, asi que este selector
              tiene que estar visible ANTES de iniciar sesion, no solo
              adentro de la plataforma ya logueado. */}
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
            <div style={{ display:'flex', background:'#F0F2F8', border:'1px solid #E2E6F0', borderRadius:8, overflow:'hidden' }}>
              <button type="button" onClick={() => setIdioma('en')}
                style={{ padding:'5px 12px', border:'none', background: idioma==='en' ? '#1B3A8C' : 'transparent', color: idioma==='en' ? '#fff' : '#6B7280', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                🇬🇧 EN
              </button>
              <button type="button" onClick={() => setIdioma('es')}
                style={{ padding:'5px 12px', border:'none', background: idioma==='es' ? '#1B3A8C' : 'transparent', color: idioma==='es' ? '#fff' : '#6B7280', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                🇪🇸 ES
              </button>
            </div>
          </div>

          {/* Boton volver - urgente #13: siempre visible, un solo boton (urgente #14: se elimino el duplicado en ingles).
              Solo se muestra aqui en la pantalla de seleccion de rol -- una vez
              se elige un rol, el formulario de login ya trae su propio boton
              "Volver" mas abajo (con logica distinta: regresa a elegir rol en
              vez de salir de la pantalla). Mostrar los dos a la vez duplicaba
              el boton. */}
          {!rol && (
            <button onClick={onVolver}
              style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#6B7280', fontFamily:'inherit', marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
              {t.volver}
            </button>
          )}

          {/* Plan elegido */}
          {planElegido && !rol && (
            <div style={{ background: planElegido.color + '15', border:'1.5px solid ' + planElegido.color + '60', borderRadius:12, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:22 }}>{planElegido.emoji}</span>
              <div>
                <div style={{ fontSize:12, color:'#6B7280' }}>{t.planSeleccionado}</div>
                <div style={{ fontSize:14, fontWeight:700, color:'#1A1A2E' }}>{planElegido.nombre} — {planElegido.precio}</div>
              </div>
            </div>
          )}

          {!rol ? (
            /* Seleccion de rol */
            <div>
              <h2 style={{ fontSize: esMovil ? '21px' : '24px', fontWeight:800, marginBottom:6 }}>{t.bienvenido}</h2>
              <p style={{ color:'#6B7280', marginBottom:24, fontSize:'14px' }}>{t.seleccionaAcceso}</p>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {ROLES.map(r => (
                  <button key={r.id} onClick={() => setRol(r.id)}
                    style={{ display:'flex', alignItems:'center', gap:16, padding: esMovil ? '14px 16px' : '16px 20px', border:'2px solid #E2E6F0', borderRadius:14, background:'#fff', cursor:'pointer', textAlign:'left', width:'100%', fontFamily:'inherit', transition:'all .15s ease' }}
                    onMouseOver={e => { e.currentTarget.style.border='2px solid ' + r.color; e.currentTarget.style.background=r.color+'10' }}
                    onMouseOut={e => { e.currentTarget.style.border='2px solid #E2E6F0'; e.currentTarget.style.background='#fff' }}>
                    <div style={{ width:44, height:44, borderRadius:12, background:r.color+'15', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                      {r.ic}
                    </div>
                    <div>
                      <div style={{ fontSize:15, fontWeight:700, color:'#1A1A2E', marginBottom:2 }}>{r.label}</div>
                      <div style={{ fontSize:12, color:'#6B7280' }}>{r.desc}</div>
                    </div>
                    <div style={{ marginLeft:'auto', fontSize:18, color:'#9CA3AF' }}>→</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Formulario login */
            <div>
              {/* Cuando no hay selector de rol al que volver (solo existe
                  Estudiante: dentro de la app, o al entrar por "Get
                  Started"), "Volver" regresa a la pantalla anterior en vez
                  de mostrar una lista de un solo boton. */}
              <button onClick={() => soloRolEstudiante ? onVolver() : (setRol(null), setError(''))}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#6B7280', fontFamily:'inherit', marginBottom:20, display:'flex', alignItems:'center', gap:6 }}>
                {t.volver}
              </button>

              {planElegido && (
                <div style={{ background: planElegido.color + '15', border:'1.5px solid ' + planElegido.color + '60', borderRadius:12, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:22 }}>{planElegido.emoji}</span>
                  <div>
                    <div style={{ fontSize:12, color:'#6B7280' }}>{t.planSeleccionado}</div>
                    <div style={{ fontSize:14, fontWeight:700, color:'#1A1A2E' }}>{planElegido.nombre} — {planElegido.precio}</div>
                  </div>
                </div>
              )}

              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                <div style={{ width:44, height:44, borderRadius:12, background:ROLES.find(r=>r.id===rol)?.color+'15', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
                  {ROLES.find(r=>r.id===rol)?.ic}
                </div>
                <div>
                  <h2 style={{ fontSize: esMovil ? '19px' : '22px', fontWeight:800, marginBottom:2 }}>
                    {t.accesoTitulo(ROLES.find(r=>r.id===rol)?.label)}
                  </h2>
                  <p style={{ color:'#6B7280', fontSize:'13px' }}>{t.ingresaCredenciales}</p>
                </div>
              </div>

              {error && (
                <div style={{ background:'#FEF2F2', color:'#991B1B', padding:'10px 14px', borderRadius:'8px', fontSize:'13px', marginBottom:'16px', border:'1px solid #FECACA' }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmitForm}>
                <div style={{ marginBottom:'14px' }}>
                  <label style={{ display:'block', fontSize:'13px', fontWeight:600, marginBottom:'5px' }}>{t.email}</label>
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #E2E6F0', borderRadius:'10px', fontFamily:'inherit', fontSize:'14px', outline:'none', boxSizing:'border-box' }}
                    placeholder={t.placeholderEmail} />
                </div>
                <div style={{ marginBottom:'20px' }}>
                  <label style={{ display:'block', fontSize:'13px', fontWeight:600, marginBottom:'5px' }}>{t.password}</label>
                  <div style={{ position:'relative' }}>
                    <input type={mostrarPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)}
                      style={{ width:'100%', padding:'10px 42px 10px 14px', border:'1.5px solid #E2E6F0', borderRadius:'10px', fontFamily:'inherit', fontSize:'14px', outline:'none', boxSizing:'border-box' }}
                      placeholder={t.placeholderPassword} />
                    <button type="button" onClick={() => setMostrarPassword(v => !v)}
                      title={mostrarPassword ? t.ocultarPassword : t.mostrarPassword}
                      style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', padding:4, display:'flex', alignItems:'center' }}>
                      <IconoOjo abierto={mostrarPassword} />
                    </button>
                  </div>
                </div>

                {/* SELECTOR DE AVATAR - solo aplica al crear cuenta nueva de estudiante */}
                {rol === 'estudiante' && modoEstudiante === 'crear' && (
                  <div style={{ marginBottom:20, border:'1.5px solid #E2E6F0', borderRadius:12, padding: esMovil ? 14 : 16, background:'#F8FAFF' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'#1A1A2E', marginBottom:4 }}>
                      {t.eligeAvatar}
                    </div>
                    <div style={{ fontSize:11, color:'#6B7280', marginBottom:12 }}>
                      {t.soloAlCrear}
                    </div>

                    {/* Tabs Emoji / Foto */}
                    <div style={{ display:'flex', gap:8, marginBottom:14 }}>
                      <button type="button" onClick={() => setTipoAvatar('emoji')}
                        style={{ flex:1, padding:'8px', borderRadius:8, border:'1.5px solid ' + (tipoAvatar==='emoji' ? '#1B3A8C' : '#E2E6F0'),
                          background: tipoAvatar==='emoji' ? '#1B3A8C' : '#fff', color: tipoAvatar==='emoji' ? '#fff' : '#374151',
                          fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                        {t.elegirEmoji}
                      </button>
                      <button type="button" onClick={() => setTipoAvatar('foto')}
                        style={{ flex:1, padding:'8px', borderRadius:8, border:'1.5px solid ' + (tipoAvatar==='foto' ? '#1B3A8C' : '#E2E6F0'),
                          background: tipoAvatar==='foto' ? '#1B3A8C' : '#fff', color: tipoAvatar==='foto' ? '#fff' : '#374151',
                          fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                        {t.subirFoto}
                      </button>
                    </div>

                    {tipoAvatar === 'emoji' ? (
                      <div>
                        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: emojiGridAbierto ? 10 : 0 }}>
                          <div style={{ width:52, height:52, borderRadius:'50%', background:'#fff', border:'2px solid #1B3A8C', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, flexShrink:0 }}>
                            {emojiElegido}
                          </div>
                          <button type="button" onClick={() => setEmojiGridAbierto(v => !v)}
                            style={{ padding:'8px 14px', borderRadius:8, border:'1.5px solid #1B3A8C', background:'#fff', color:'#1B3A8C', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                            {emojiGridAbierto ? t.ocultar : t.seleccionarEmoji}
                          </button>
                        </div>
                        {emojiGridAbierto && (
                          <div style={{ display:'grid', gridTemplateColumns: esMovil ? 'repeat(6,1fr)' : 'repeat(8,1fr)', gap:6 }}>
                            {EMOJIS_AVATAR.map(emoji => (
                              <button key={emoji} type="button" onClick={() => { setEmojiElegido(emoji); setEmojiGridAbierto(false) }}
                                style={{ width:'100%', aspectRatio:'1', borderRadius:8, border:'1.5px solid ' + (emojiElegido===emoji ? '#1B3A8C' : '#E2E6F0'),
                                  background: emojiElegido===emoji ? '#EEF2FF' : '#fff', fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                {emoji}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ textAlign:'center' }}>
                        {previewFoto ? (
                          <img src={previewFoto} alt="Preview" style={{ width:80, height:80, borderRadius:'50%', objectFit:'cover', border:'2px solid #1B3A8C', marginBottom:10 }} />
                        ) : (
                          <div style={{ width:80, height:80, borderRadius:'50%', background:'#E2E6F0', margin:'0 auto 10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, color:'#9CA3AF' }}>
                            📷
                          </div>
                        )}
                        <label style={{ display:'inline-block', padding:'8px 16px', background:'#fff', border:'1.5px solid #1B3A8C', color:'#1B3A8C', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                          {archivoFoto ? t.cambiarFoto : t.seleccionarFoto}
                          <input type="file" accept="image/*" onChange={handleSeleccionarFoto} style={{ display:'none' }} />
                        </label>
                        <div style={{ fontSize:11, color:'#9CA3AF', marginTop:6 }}>{t.formatoFoto}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Un estudiante nuevo (todavia sin cuenta confirmada) solo ve
                    "Create free account" -- no tiene sentido ofrecerle
                    "Ingresar" para una cuenta que no existe. En cuanto
                    confirma su correo, este boton reaparece solo. */}
                {(rol !== 'estudiante' || modoEstudiante === 'ingresar') && (
                  <button type="submit" disabled={carga}
                    style={{ width:'100%', padding:'12px', background: ROLES.find(r=>r.id===rol)?.color || '#1B3A8C', color:'#fff', border:'none', borderRadius:'10px', fontFamily:'inherit', fontSize:'15px', fontWeight:700, cursor:'pointer', marginBottom:'10px' }}>
                    {carga ? t.cargando : t.ingresarComoTitulo(ROLES.find(r=>r.id===rol)?.label)}
                  </button>
                )}
                {rol === 'estudiante' && modoEstudiante === 'crear' && (
                  <button type="button" onClick={handleRegistro} disabled={carga || subiendoFoto}
                    style={{ width:'100%', padding:'11px', background:'transparent', color:'#1B3A8C', border:'1.5px solid #E2E6F0', borderRadius:'10px', fontFamily:'inherit', fontSize:'15px', fontWeight:600, cursor:'pointer' }}>
                    {subiendoFoto ? t.subiendoFoto : t.crearCuentaGratis}
                  </button>
                )}

                {/* Enlace para cambiar de modo a mano (cuenta ya existente
                    entrando desde otro dispositivo, o al reves). */}
                {rol === 'estudiante' && (
                  <button type="button"
                    onClick={() => { setModoEstudiante(m => m === 'crear' ? 'ingresar' : 'crear'); setError('') }}
                    style={{ width:'100%', textAlign:'center', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, color:'#6B7280', marginTop:12, textDecoration:'underline' }}>
                    {modoEstudiante === 'crear' ? t.yaTienesCuenta : t.eresNuevo}
                  </button>
                )}
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
